import useQueryParams from '@common/src/hooks/useQueryParams';
import { AnvaFlowsInsuranceTag } from '@flows/src/api/anva-flows/getAnvaFlowsInsuranceTags';
import {
  AnvaProduct,
  useAnvaProducts,
} from '@flows/src/api/anva-flows/getAnvaProducts';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useAnvaFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/anvaFlowsConfigureStore';
import { PartyType } from '@parties/src/types';
import QuestionViewSkeleton from '@sales-conversation/src/components/skeletons/QuestionViewSkeleton';
import useStartAnvaFlow, {
  AnvaFlowProduct,
} from '@sales-conversation/src/hooks/anva/useStartAnvaFlow';
import { FC, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';

interface getProductsFromConfigProps {
  catalogue: AnvaProduct[];
  insuranceGroups: AnvaFlowsInsuranceTag[];
  companies: Record<string, string[]>;
  coverages: Record<string, string[]>;
}

const getProductsFromConfig = (
  params: getProductsFromConfigProps,
): AnvaFlowProduct[] => {
  const { catalogue } = params;
  const {
    insuranceGroups: chosenInsuranceGroups,
    companies,
    coverages,
  } = params;

  // First, determine the status of each selected company based on their products
  const companyStatusMap: Record<string, string> = {};

  // Build company status map by analyzing all products for each insurance group
  // Using the same logic as the UI to ensure consistency
  chosenInsuranceGroups.forEach((group) => {
    const selectedCompanyIds = companies[group.id] || [];

    selectedCompanyIds.forEach((companyId) => {
      // Find all products for this company in this insurance group
      const companyProducts = catalogue.filter(
        (product) =>
          product.insurance_company.id === companyId &&
          group.insurance_ids.includes(Number(product.insurance.id)),
      );

      // Determine company status using the same logic as UI
      // Prioritize ACTIVE > UNDER_CONSTRUCTION > DISABLED
      const statusMap: Record<string, string> = {};

      companyProducts.forEach((product) => {
        if (
          !statusMap[companyId] ||
          (statusMap[companyId] === 'DISABLED' &&
            product.status !== 'DISABLED') ||
          (statusMap[companyId] === 'UNDER_CONSTRUCTION' &&
            product.status === 'ACTIVE')
        ) {
          statusMap[companyId] = product.status;
        }
      });

      companyStatusMap[companyId] = statusMap[companyId] || 'DISABLED';
    });
  });

  // Now iterate through the product catalogue to filter and map products
  return catalogue.reduce((acc, product) => {
    const {
      insurance,
      insurance_company,
      coverages: productCoverages,
      status: productStatus,
    } = product;

    // Find the insurance group that matches the current product's insurance ID
    const foundGroup = chosenInsuranceGroups.find(({ insurance_ids }) =>
      insurance_ids.includes(Number(insurance.id)),
    );

    // Check if this company is selected and get its status
    const isCompanySelected =
      foundGroup && companies[foundGroup.id]?.includes(insurance_company.id);

    if (!isCompanySelected || !foundGroup) {
      return acc;
    }

    const companyStatus = companyStatusMap[insurance_company.id];

    // Filter products based on company status:
    // - ACTIVE company: include ACTIVE and UNDER_CONSTRUCTION products
    // - UNDER_CONSTRUCTION company: include only UNDER_CONSTRUCTION products
    // - DISABLED company: include only DISABLED products
    let shouldIncludeProduct = false;

    if (companyStatus === 'ACTIVE') {
      shouldIncludeProduct =
        productStatus === 'ACTIVE' || productStatus === 'UNDER_CONSTRUCTION';
    } else if (companyStatus === 'UNDER_CONSTRUCTION') {
      shouldIncludeProduct = productStatus === 'UNDER_CONSTRUCTION';
    } else if (companyStatus === 'DISABLED') {
      shouldIncludeProduct = productStatus === 'DISABLED';
    }

    if (!shouldIncludeProduct) {
      return acc;
    }

    // Create a flow product object with the product ID and associated coverages
    const flowProduct = {
      id: product.id,
      coverages: foundGroup.id
        ? coverages[foundGroup.id]?.filter((code) =>
            productCoverages.some(
              ({ code: productCode }) => productCode === code,
            ),
          )
        : [],
    };

    acc.push(flowProduct);
    return acc;
  }, [] as AnvaFlowProduct[]);
};

const FlowsConfigureFinish: FC = () => {
  const queryParams = useQueryParams();
  const partyType = (queryParams.get('type') as PartyType) || 'CUSTOMER';
  const { data: catalogue, isSuccess } = useAnvaProducts(partyType);
  const startAnvaFlow = useStartAnvaFlow();
  const { flowId } = useParams<{ flowId: string }>();
  const history = useHistory();

  useEffect(() => {
    // If the required data is not available, exit early
    if (!catalogue || !flowId) return;

    // Get the current state from the flows configure store
    const state = useAnvaFlowsConfigureStore.getState();

    // If no insurances are selected, redirect the user back to the configuration page
    if (!state.insurances)
      return history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}`,
        search: window.location.search,
      });

    // Extract the list of products from the catalogue
    const { items } = catalogue;

    // Generate the list of products based on the selected insurances, companies, and coverages
    const products = getProductsFromConfig({
      catalogue: items,
      insuranceGroups: state.insurances,
      companies: state.companies,
      coverages: state.coverages,
    });

    // Start the ANVA flow with the generated products and other required parameters
    // Preserve the type query parameter in the inputUrl for back navigation
    const currentType = queryParams.get('type');
    const inputUrl = currentType
      ? `${FLOWS_CONFIG_PATH}/${flowId}?type=${currentType}`
      : `${FLOWS_CONFIG_PATH}/${flowId}`;

    startAnvaFlow({
      flowId: flowId,
      inputUrl,
      partyId: queryParams.get('partyId') || undefined,
      anva_products: products,
    });
  }, [isSuccess]);

  return <QuestionViewSkeleton />;
};

export default FlowsConfigureFinish;
